import { NavLink, Route, Routes, Navigate, Link } from "react-router-dom";
import { LogOut, Calendar, ShoppingBag, ClipboardList, Cake, Send, Settings, Image as ImageIcon, Package, PawPrint } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { LOGO_URL } from "@/lib/brand";
import AdminEvents from "@/pages/admin/AdminEvents";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminBookings from "@/pages/admin/AdminBookings";
import AdminBirthdays from "@/pages/admin/AdminBirthdays";
import AdminBroadcast from "@/pages/admin/AdminBroadcast";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminGallery from "@/pages/admin/AdminGallery";
import AdminShopOrders from "@/pages/admin/AdminShopOrders";
import AdminPetRecords from "@/pages/admin/AdminPetRecords";

const NAV = [
  { to: "/admin", label: "Events", icon: Calendar, end: true },
  { to: "/admin/gallery", label: "Past Gallery", icon: ImageIcon },
  { to: "/admin/products", label: "Shop", icon: ShoppingBag },
  { to: "/admin/shop-orders", label: "Shop Orders", icon: Package },
  { to: "/admin/bookings", label: "Bookings", icon: ClipboardList },
  { to: "/admin/pet-records", label: "Pet Records", icon: PawPrint },
  { to: "/admin/birthdays", label: "Birthdays", icon: Cake },
  { to: "/admin/broadcast", label: "Broadcast", icon: Send },
  { to: "/admin/settings", label: "Site Settings", icon: Settings },
];

export default function AdminDashboard() {
  const { username, logout } = useAuth();

  return (
    <div className="min-h-screen bg-brand-ivory flex flex-col md:flex-row" data-testid="admin-dashboard">
      <aside className="md:w-64 md:min-h-screen bg-brand-soft text-brand-cream md:sticky md:top-0">
        <Link to="/" className="flex items-center gap-3 px-6 py-6 border-b border-brand-cream/10">
          <img src={LOGO_URL} alt="Furshala" className="w-12 h-12 object-contain" />
          <div>
            <div className="font-serif text-xl">Furshala</div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-brand-gold">Admin</div>
          </div>
        </Link>
        <nav className="px-4 py-5 flex md:flex-col gap-1 overflow-x-auto md:overflow-visible">
          {NAV.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                className={({ isActive }) => `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm whitespace-nowrap transition-colors ${isActive ? "bg-brand-maroon text-brand-cream" : "text-brand-cream/70 hover:bg-brand-cream/5"}`}
                data-testid={`admin-nav-${n.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
              >
                <Icon className="w-4 h-4" /> {n.label}
              </NavLink>
            );
          })}
        </nav>
        <div className="mt-auto md:absolute md:bottom-0 md:left-0 md:right-0 p-5 border-t border-brand-cream/10 hidden md:block">
          <div className="text-xs text-brand-cream/60 mb-2">Signed in as</div>
          <div className="text-sm font-medium text-brand-cream truncate">{username}</div>
          <button onClick={logout} className="mt-3 inline-flex items-center gap-2 text-sm text-brand-saffron hover:text-brand-saffron/80" data-testid="admin-logout">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 p-6 sm:p-10 max-w-6xl mx-auto w-full">
        <Routes>
          <Route index element={<AdminEvents />} />
          <Route path="gallery" element={<AdminGallery />} />
          <Route path="products" element={<AdminProducts />} />
          <Route path="shop-orders" element={<AdminShopOrders />} />
          <Route path="bookings" element={<AdminBookings />} />
          <Route path="pet-records" element={<AdminPetRecords />} />
          <Route path="birthdays" element={<AdminBirthdays />} />
          <Route path="broadcast" element={<AdminBroadcast />} />
          <Route path="settings" element={<AdminSettings />} />
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Routes>
        <button onClick={logout} className="md:hidden mt-10 inline-flex items-center gap-2 text-sm text-brand-maroon" data-testid="admin-logout-mobile">
          <LogOut className="w-4 h-4" /> Sign out
        </button>
      </main>
    </div>
  );
}
